/* ==========================================================================
   Zaago — theme behaviour
   --------------------------------------------------------------------------
   Progressive enhancement only. Everything here upgrades markup that already
   works: the page renders, reads and converts with JS switched off.
   ========================================================================== */

(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------- reveals */

  function initReveals() {
    const targets = document.querySelectorAll('.reveal');
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) return;

    // Hide them only now that we know we can bring them back.
    document.documentElement.classList.add('reveals-on');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    targets.forEach((el) => observer.observe(el));
  }

  /* ------------------------------------------------------- quantity input */

  function initQuantity() {
    document.querySelectorAll('[data-qty]').forEach((widget) => {
      const input = widget.querySelector('input');
      if (!input) return;

      widget.addEventListener('click', (event) => {
        const button = event.target.closest('[data-qty-step]');
        if (!button) return;

        const step = Number(button.dataset.qtyStep);
        const min = Number(input.min) || 1;
        const max = Number(input.max) || 99;
        const next = Math.min(max, Math.max(min, (Number(input.value) || min) + step));

        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* ------------------------------------------------------------ accordion */

  /**
   * FAQ accordions use <details>, so they already open without JS. This only
   * adds the one-at-a-time behaviour and smooth height.
   */
  function initAccordion() {
    document.querySelectorAll('[data-accordion]').forEach((group) => {
      const items = Array.from(group.querySelectorAll('details'));

      items.forEach((item) => {
        item.addEventListener('toggle', () => {
          if (!item.open) return;
          items.forEach((other) => {
            if (other !== item) other.open = false;
          });
        });
      });
    });
  }

  /* ----------------------------------------------------------- sticky ATC */

  /**
   * The mobile buy bar appears once the real add-to-cart button scrolls out of
   * view, and hides again when it returns — so there are never two competing
   * primary actions on screen at once.
   */
  function initStickyAtc() {
    const bar = document.querySelector('[data-sticky-atc]');
    const anchor = document.querySelector('[data-atc-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const passed = !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', passed);
      },
      { threshold: 0 }
    );

    observer.observe(anchor);
  }

  /* --------------------------------------------------------- media gallery */

  function initGallery() {
    document.querySelectorAll('[data-gallery]').forEach((gallery) => {
      const main = gallery.querySelector('[data-gallery-main]');
      const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
      if (!main || !thumbs.length) return;

      const prev = gallery.querySelector('[data-gallery-prev]');
      const next = gallery.querySelector('[data-gallery-next]');
      const counter = gallery.querySelector('[data-gallery-counter]');
      let current = 0;

      function select(index) {
        const thumb = thumbs[index];
        if (!thumb) return;
        current = index;

        // srcset wins over src, so both have to be replaced — setting src
        // alone leaves the browser showing the previous image's candidates.
        if (thumb.dataset.srcset) main.srcset = thumb.dataset.srcset;
        else main.removeAttribute('srcset');
        main.src = thumb.dataset.full || thumb.querySelector('img').src;
        main.alt = thumb.dataset.alt || '';

        thumbs.forEach((t, i) => {
          const active = i === index;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', String(active));
          t.tabIndex = active ? 0 : -1;
        });

        if (counter) counter.textContent = `${index + 1} / ${thumbs.length}`;
        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index === thumbs.length - 1;

        // Keep the active thumbnail in view in the scrolling strip.
        thumb.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }

      const step = (delta) => select(Math.min(thumbs.length - 1, Math.max(0, current + delta)));

      prev?.addEventListener('click', () => step(-1));
      next?.addEventListener('click', () => step(1));

      thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => select(index));

        // Roving tabindex — arrow keys move through the thumbnail strip.
        thumb.addEventListener('keydown', (event) => {
          const delta = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
          if (!delta) return;
          event.preventDefault();
          const target = (index + delta + thumbs.length) % thumbs.length;
          select(target);
          thumbs[target].focus();
        });
      });

      select(0);
    });
  }

  /* ------------------------------------------------------------ card fan */

  /**
   * The sample-card fan tilts a few degrees toward the pointer. Desktop and
   * fine-pointer only — on touch it would just fight the scroll.
   */
  function initTilt() {
    if (reduceMotion || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    document.querySelectorAll('[data-tilt]').forEach((el) => {
      const strength = Number(el.dataset.tilt) || 6;
      let frame = null;

      el.addEventListener('pointermove', (event) => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          const rect = el.getBoundingClientRect();
          const x = (event.clientX - rect.left) / rect.width - 0.5;
          const y = (event.clientY - rect.top) / rect.height - 0.5;
          el.style.setProperty('--tilt-x', `${(-y * strength).toFixed(2)}deg`);
          el.style.setProperty('--tilt-y', `${(x * strength).toFixed(2)}deg`);
          frame = null;
        });
      });

      el.addEventListener('pointerleave', () => {
        el.style.setProperty('--tilt-x', '0deg');
        el.style.setProperty('--tilt-y', '0deg');
      });
    });
  }

  /* -------------------------------------------------------- floating CTA */

  /**
   * Visible only in the middle of the page: after the opening screen has gone,
   * and not once the footer is on screen. Without the second rule it parks on
   * top of the newsletter form, which is its own conversion point.
   */
  function initFloatingCta() {
    const cta = document.querySelector('[data-floating-cta]');
    if (!cta) return;

    cta.hidden = false;

    // The tail of the page has its own conversion point (the newsletter form),
    // so the floating button stands down once that is reached.
    const tail = document.querySelector('.newsletter') || document.querySelector('.site-footer');
    let frame = null;

    function update() {
      frame = null;
      const viewport = window.innerHeight;
      const pastFirstScreen = window.scrollY > viewport * 0.8;
      const atTail = tail ? tail.getBoundingClientRect().top < viewport * 0.75 : false;
      cta.classList.toggle('is-visible', pastFirstScreen && !atTail);
    }

    function schedule() {
      if (frame === null) frame = requestAnimationFrame(update);
    }

    // Measured from scroll position rather than an IntersectionObserver: the
    // state has to be correct on first paint too, including when the page is
    // opened directly on a #hash deep in the document.
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    update();

    // On a product page the button is a shortcut to the real buy box.
    const buy = cta.querySelector('[data-floating-buy]');
    const form = document.querySelector('.pdp__pricebox form');
    if (buy && form) {
      buy.addEventListener('click', (event) => {
        event.preventDefault();
        form.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        form.querySelector('[type="submit"]')?.focus({ preventScroll: true });
      });
    }
  }

  /* ---------------------------------------------------------- scrollers */

  /** Enables/disables the arrow buttons on a horizontal scroller. */
  function initScrollers() {
    document.querySelectorAll('[data-scroller]').forEach((wrapper) => {
      const track = wrapper.querySelector('.scroller');
      const prev = wrapper.querySelector('[data-scroll-prev]');
      const next = wrapper.querySelector('[data-scroll-next]');
      if (!track) return;

      const controls = wrapper.querySelector('[data-scroll-prev]')?.parentElement;

      function update() {
        const max = track.scrollWidth - track.clientWidth - 2;

        // Nothing overflows — the arrows would only ever render disabled.
        if (controls) controls.hidden = max <= 0;

        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      }

      function page(direction) {
        const card = track.querySelector(':scope > *');
        const amount = card ? card.offsetWidth + 16 : track.clientWidth * 0.8;
        track.scrollBy({ left: amount * direction, behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      prev?.addEventListener('click', () => page(-1));
      next?.addEventListener('click', () => page(1));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
    });
  }

  /* --------------------------------------------------------------- cart */

  /**
   * Slide-out cart.
   *
   * State lives on the server: every mutation posts to the Cart AJAX API and
   * then re-fetches `?sections=cart-drawer`, so the drawer's markup always
   * comes from the same Liquid the page rendered. There is no client-side
   * template to fall out of sync with the section file.
   */
  function initCart() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    let lastFocus = null;
    const panel = () => drawer.querySelector('.drawer__panel');

    /* ------------------------------------------------------------- open/close */

    function open() {
      lastFocus = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      panel()?.focus();
    }

    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (lastFocus instanceof HTMLElement) lastFocus.focus();
    }

    const isOpen = () => drawer.classList.contains('is-open');

    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-open]')) {
        event.preventDefault();
        open();
        return;
      }
      if (isOpen() && event.target.closest('[data-drawer-close]')) {
        event.preventDefault();
        close();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (!isOpen()) return;

      if (event.key === 'Escape') {
        close();
        return;
      }

      // Keep Tab inside the panel while it is modal.
      if (event.key !== 'Tab') return;
      const focusable = panel()?.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable || !focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    /* ---------------------------------------------------------------- refresh */

    const SECTION = 'cart-drawer';

    function render(markup) {
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const fresh = parsed.querySelector('[data-cart-drawer]');
      if (!fresh) throw new Error('Cart markup missing');

      const wasOpen = isOpen();
      drawer.innerHTML = fresh.innerHTML;
      drawer.classList.toggle('is-open', wasOpen);
      drawer.setAttribute('aria-hidden', wasOpen ? 'false' : 'true');
      // The button that had focus was just replaced; keep focus in the dialog.
      if (wasOpen) panel()?.focus({ preventScroll: true });

      syncCount(parsed);
    }

    /**
     * Fallback for responses that did not bundle the section. `no-store` plus a
     * unique URL, because a cached copy here shows the cart as it was before
     * the change — which reads to a shopper as "it didn't add", so they tap again.
     */
    async function refresh() {
      const response = await fetch(`${window.location.pathname}?sections=${SECTION}&_=${Date.now()}`, {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Could not refresh the cart');
      const data = await response.json();
      if (!data[SECTION]) throw new Error('Cart markup missing');
      render(data[SECTION]);
    }

    /** Header badge, read out of the refreshed markup rather than recomputed. */
    function syncCount(parsed) {
      const badge = document.querySelector('[data-cart-count]');
      if (!badge) return;
      const count = parsed.querySelector('.drawer__count');
      const value = count ? count.textContent.trim() : '0';
      badge.textContent = value;
      badge.hidden = value === '0' || value === '';
    }

    function showError(message) {
      const slot = drawer.querySelector('[data-cart-error]');
      if (!slot) return;
      slot.textContent = message;
      slot.hidden = !message;
    }

    /**
     * Posts to the Cart API and asks for the drawer in the same response
     * (Shopify's bundled section rendering): one round trip instead of two,
     * and the markup can never be a cached copy.
     */
    async function send(url, body) {
      const init = { method: 'POST', headers: { Accept: 'application/json' } };
      if (body instanceof FormData) {
        body.append('sections', SECTION);
        body.append('sections_url', window.location.pathname);
        init.body = body;
      } else {
        init.headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify({ ...body, sections: SECTION, sections_url: window.location.pathname });
      }

      const response = await fetch(url, init);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.description || data.message || 'Could not update your cart. Please try again.');
      }

      if (data.sections && data.sections[SECTION]) render(data.sections[SECTION]);
      else await refresh();
    }

    // Changes run one at a time, in order. Line numbers and quantities come
    // from the markup, so a second request built from stale markup would undo
    // or double the first.
    let queue = Promise.resolve();

    /** Resolves true on success; never rejects. */
    function mutate(url, body) {
      const run = async () => {
        drawer.classList.add('is-busy');
        showError('');
        try {
          await send(url, body);
          return true;
        } catch (error) {
          console.error(error);
          showError(error.message);
          return false;
        } finally {
          drawer.classList.remove('is-busy');
        }
      };
      const result = queue.then(run);
      queue = result;
      return result;
    }

    /* ---------------------------------------------------------- interactions */

    // Quantity, remove, and the cross-sell "Add" buttons.
    drawer.addEventListener('click', (event) => {
      const line = event.target.closest('[data-line-change]');
      if (line) {
        mutate('/cart/change.js', {
          line: Number(line.dataset.line),
          quantity: Number(line.dataset.lineChange),
        });
        return;
      }

      const bundle = event.target.closest('[data-bundle-add]');
      if (bundle) {
        event.preventDefault();
        addBundle(bundle);
        return;
      }

      const add = event.target.closest('[data-cart-add]');
      if (add) {
        add.setAttribute('aria-busy', 'true');
        mutate('/cart/add.js', { id: Number(add.dataset.cartAdd), quantity: 1 }).finally(() => {
          add.removeAttribute('aria-busy');
        });
      }
    });

    /** One request containing every title in the set. */
    async function addBundle(trigger) {
      const ids = String(trigger.dataset.bundleAdd || '')
        .split(',')
        .map((id) => Number(id.trim()))
        .filter(Boolean);
      if (!ids.length || trigger.getAttribute('aria-busy') === 'true') return;

      const label = trigger.innerHTML;
      trigger.setAttribute('aria-busy', 'true');
      trigger.innerHTML = 'Adding…';
      try {
        await mutate('/cart/add.js', { items: ids.map((id) => ({ id, quantity: 1 })) });
        open();
      } finally {
        trigger.removeAttribute('aria-busy');
        trigger.innerHTML = label;
      }
    }

    document.addEventListener('click', (event) => {
      const bundle = event.target.closest('[data-bundle-add]');
      if (!bundle || bundle.closest('[data-cart-drawer]')) return;
      event.preventDefault();
      addBundle(bundle);
    });

    // Product forms add without a page reload, then reveal the drawer — with
    // the new line in it, or with the reason it could not be added.
    document.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.action || !form.action.includes('/cart/add')) return;

      event.preventDefault();
      const button = form.querySelector('[type="submit"]');
      if (button?.disabled) return;
      const original = button ? button.innerHTML : '';
      if (button) {
        button.disabled = true;
        button.innerHTML = 'Adding…';
      }

      try {
        await mutate('/cart/add.js', new FormData(form));
        open();
      } finally {
        if (button) {
          button.disabled = false;
          button.innerHTML = original;
        }
      }
    });
  }

  /* ---------------------------------------------------------------- init */

  function init() {
    initReveals();
    initQuantity();
    initAccordion();
    initStickyAtc();
    initGallery();
    initTilt();
    initScrollers();
    initFloatingCta();
    initCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
